import type { SupabaseClient } from '@supabase/supabase-js'
import { OPENING_BALANCE_GRANT_SOURCE, OPERATION_TYPES } from '../constants'
import type { BalanceGrant, BillingOverviewResponse, WalletGrant } from '../types'

type BalanceDetails = Pick<BillingOverviewResponse['balance'], 'grants' | 'nextExpiry'>

/** Spendable grants with their plan names, and the next expiry, for the balance card. */
export async function describeBalanceGrants(
  supabase: SupabaseClient,
  grants: WalletGrant[],
): Promise<BalanceDetails> {
  const planNames = await loadPlanNames(supabase, grants)
  return {
    grants: grants.map((grant): BalanceGrant => ({
      source: grant.source,
      planId: grant.planId,
      planName: grant.planId ? planNames.get(grant.planId) ?? null : null,
      remainingMicrousd: grant.remainingMicrousd,
      expiresAt: grant.expiresAt,
    })),
    nextExpiry: findNextExpiry(grants),
  }
}

/** Grants arrive in spending order, so the first one with an expiry expires soonest. */
export function findNextExpiry(
  grants: Pick<WalletGrant, 'expiresAt' | 'remainingMicrousd'>[],
): BalanceDetails['nextExpiry'] {
  const expiresAt = grants.find(grant => grant.expiresAt !== null)?.expiresAt
  if (!expiresAt) return null
  const amountMicrousd = grants
    .filter(grant => grant.expiresAt === expiresAt)
    .reduce((sum, grant) => sum + grant.remainingMicrousd, 0)
  return { expiresAt, amountMicrousd }
}

/** When a plan's payment credits run out; null without them, or when any of them never expires. */
export function planCreditsExpireAt(grants: BalanceGrant[], planId: string): string | null {
  const planCredits = grants.filter(grant => grant.source === OPERATION_TYPES.PAYMENT_CREDIT && grant.planId === planId)
  const expiries = planCredits.flatMap(grant => (grant.expiresAt ? [grant.expiresAt] : []))
  if (expiries.length === 0 || expiries.length < planCredits.length) return null
  return expiries.reduce((latest, expiresAt) => (Date.parse(expiresAt) > Date.parse(latest) ? expiresAt : latest))
}

/** The customer-facing name of where credits came from, shared by the balance details and notices. */
export function grantSourceLabel(source: string, planName: string | null): string {
  switch (source) {
    case OPERATION_TYPES.INITIAL_GRANT:
      return withPlanName('Starting credits', planName)
    case OPERATION_TYPES.PAYMENT_CREDIT:
      return withPlanName('Plan credits', planName)
    case OPERATION_TYPES.TOPUP:
      return 'Top-up'
    case OPERATION_TYPES.MANUAL_GRANT:
      return 'Platform grant'
    case OPERATION_TYPES.PARTNER_TRANSFER:
      return 'Partner earnings'
    case OPERATION_TYPES.REFERRAL_BONUS:
      return 'Referral bonus'
    case OPENING_BALANCE_GRANT_SOURCE:
      return 'Earlier balance'
    default:
      return 'Credits'
  }
}

function withPlanName(label: string, planName: string | null): string {
  return planName ? `${label} · ${planName}` : label
}

async function loadPlanNames(supabase: SupabaseClient, grants: WalletGrant[]): Promise<Map<string, string>> {
  const planIds = [...new Set(grants.flatMap(grant => (grant.planId ? [grant.planId] : [])))]
  if (planIds.length === 0) return new Map()

  const { data, error } = await supabase.from('billing_plans').select('id, display_name').in('id', planIds)
  if (error) throw new Error(`Failed to load plan names: ${error.message}`)
  return new Map((data as { id: string; display_name: string }[]).map(plan => [plan.id, plan.display_name]))
}
